import React from 'react';
import { Rect, SchematicShell } from './SchematicPrimitives';
import {
  EXHAUST_FAN,
  SMOKE_DETECTOR,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematics for the ceiling devices from the Electrical tray —
 * the exhaust fan and the smoke / CO detector. The 2D twins of
 * `exhaustFanRenderer` / `smokeDetectorRenderer`: both sides read
 * `EXHAUST_FAN` / `SMOKE_DETECTOR` in proceduralSpecs.
 *
 * Like `CeilingLightSchematic`, each mounts face-down on the ceiling, so every
 * strip is a SIDE profile with the ceiling at the top of the bbox (SVG y = 0).
 * Wall elevations never draw them (they are ceiling-mounted); the procedural
 * gallery and any section view do.
 */

/**
 * Exhaust fan. Front (looking along depth): the housing flange over one
 * grille band — the slats run along the width and overlap edge-on. Side
 * (looking along width): the flange over the individual slat ends.
 */
export function ExhaustFanSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const side = view === 'left' || view === 'right';
  const horizontal = side ? depth : width;
  const frameH = height * EXHAUST_FAN.frameHeightFrac;
  const grilleH = height * EXHAUST_FAN.grilleHeightFrac;
  const inner = horizontal * EXHAUST_FAN.grilleInsetFrac;
  const left = (horizontal - inner) / 2;
  const stroke = { strokeColor, strokeWidth };

  let grille;
  if (side) {
    const pitch = inner / EXHAUST_FAN.slatCount;
    const slat = pitch * EXHAUST_FAN.slatFillFrac;
    grille = Array.from({ length: EXHAUST_FAN.slatCount }, (_, i) => (
      <Rect
        key={i}
        x={left + pitch * (i + 0.5) - slat / 2}
        y={frameH}
        width={slat}
        height={grilleH}
        {...stroke}
      />
    ));
  } else {
    grille = (
      <Rect x={left} y={frameH} width={inner} height={grilleH} {...stroke} />
    );
  }

  return (
    <SchematicShell
      width={horizontal}
      height={height}
      drawFrame={false}
      {...stroke}
    >
      <Rect x={0} y={0} width={horizontal} height={frameH} {...stroke} />
      {grille}
    </SchematicShell>
  );
}
ExhaustFanSchematic.supportsSideViews = true;

/**
 * Smoke / CO detector: a tapered body against the ceiling with the vented
 * disc below it. Radially symmetric, so every view is the same profile over
 * its own horizontal extent.
 */
export function SmokeDetectorSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const horizontal = view === 'left' || view === 'right' ? depth : width;
  const diameter = Math.min(width, depth);
  const radius = diameter / 2;
  const cx = horizontal / 2;
  const bodyH = height * SMOKE_DETECTOR.bodyHeightFrac;
  const bottomR = radius * SMOKE_DETECTOR.bodyBottomRadiusFrac;
  const ventR = radius * SMOKE_DETECTOR.ventRadiusFrac;
  const ventH = height * SMOKE_DETECTOR.ventHeightFrac;
  const stroke = { strokeColor, strokeWidth };

  return (
    <SchematicShell
      width={horizontal}
      height={height}
      drawFrame={false}
      {...stroke}
    >
      <polygon
        points={[
          [cx - radius, 0],
          [cx + radius, 0],
          [cx + bottomR, bodyH],
          [cx - bottomR, bodyH],
        ]
          .map(([x, y]) => `${x},${y}`)
          .join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <Rect
        x={cx - ventR}
        y={bodyH}
        width={ventR * 2}
        height={ventH}
        {...stroke}
      />
    </SchematicShell>
  );
}
SmokeDetectorSchematic.supportsSideViews = true;
